import React, { useMemo } from 'react';
import { Button, Dropdown } from 'antd';
import { useQuery, useMutation, useNavigate } from '@common/hooks';
import {
  AddButton,
  ErrorHandler,
  Gutter,
  Icon,
  Modal,
  Portal,
  RenderFilteredTable,
  Text,
} from '@common/components';
import { buildDialogUrl } from '@common/services';
import { useCurrentMembership } from '@identity/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import { ARCHIVE_UNIT_SHIFT_GAP_TAG_MUTATION } from '@scheduling/graphql/mutations/unit-shift-gap-tag';
import {
  UNIT_SHIFT_GAP_TAGS_QUERY,
  UnitShiftGapTagType,
} from '@scheduling/graphql/queries';

export function UnitShiftGapTagsTable({
  organizationUnitId,
  triggerPortal,
  isMembershipLinkedToUnit = true,
}: {
  organizationUnitId: string;
  triggerPortal?: boolean;
  isMembershipLinkedToUnit?: boolean;
}) {
  const navigate = useNavigate();
  const { canShiftGapTagCreate, canShiftGapTagUpdate } = useCurrentMembership();

  const { data, error, loading, refetch } = useQuery(
    UNIT_SHIFT_GAP_TAGS_QUERY,
    { variables: { organizationUnitId, includeArchived: true } }
  );

  const [archiveTagMutation] = useMutation(ARCHIVE_UNIT_SHIFT_GAP_TAG_MUTATION);

  const unitTags = useMemo(
    () => data?.shiftGapTags?.filter((tag) => tag.scope === 'unit') ?? [],
    [data]
  );

  function openCreateDrawer() {
    navigate(
      buildDialogUrl('unit-shift-gap-tag-create-drawer', {
        props: { organizationUnitId },
      })
    );
  }

  function openUpdateDrawer(tag: UnitShiftGapTagType) {
    navigate(
      buildDialogUrl('unit-shift-gap-tag-update-drawer', {
        props: {
          tagId: tag.id,
          tagName: tag.name,
          tagDescription: tag.description || '',
        },
      })
    );
  }

  async function archivingAction({
    tagId,
    archive,
  }: {
    tagId: string;
    archive: boolean;
  }) {
    const action = archive ? 'archived' : 'unarchived';
    const errorMessage = () =>
      createMessage(
        'error',
        'Something went wrong. Try again later or contact support.'
      );

    try {
      const response = await archiveTagMutation({
        variables: {
          input: { shiftGapTagId: tagId, archive },
        },
      });

      const payloadError = response?.data?.archiveShiftGapTag?.errors;
      if (payloadError?.length) {
        createMessage(
          'error',
          payloadError.flatMap((e) => e.fullErrors).join(', ')
        );
        return;
      }

      if (response?.errors?.length) {
        createMessage(
          'error',
          response.errors.map((e) => e.message).join(', ')
        );
        return;
      }

      createMessage('success', `Shift tag ${action} successfully.`);
      refetch();
    } catch {
      errorMessage();
    }
  }

  function buildMenuItemsByTag(tag: UnitShiftGapTagType) {
    return buildMenuItems({
      tag,
      onArchive: () => archivingAction({ tagId: tag.id, archive: true }),
      onUnarchive: () => archivingAction({ tagId: tag.id, archive: false }),
      onEdit: () => openUpdateDrawer(tag),
    });
  }

  const columns = [
    {
      key: 'name',
      width: '150px',
      dataIndex: 'name',
      title: 'Name',
      sorter: (a: UnitShiftGapTagType, b: UnitShiftGapTagType) =>
        a.name.localeCompare(b.name),
    },
    {
      key: 'description',
      dataIndex: 'description',
      title: 'Description',
      render: (description: string | null) => description || 'N/A',
      sorter: (a: UnitShiftGapTagType, b: UnitShiftGapTagType) =>
        a.description?.localeCompare(b.description || '') ?? 0,
    },
    {
      key: 'status',
      title: 'Status',
      render: (_: unknown, tag: UnitShiftGapTagType) => (
        <Gutter direction="right">
          {tag.archivedAt ? (
            <Text color="midnightLighter" bold>
              Archived
            </Text>
          ) : (
            <Text color="meadowDarker" bold>
              Active
            </Text>
          )}
        </Gutter>
      ),
      sorter: (a: UnitShiftGapTagType, b: UnitShiftGapTagType) =>
        (a.archivedAt ? 1 : 0) - (b.archivedAt ? 1 : 0),
    },
    ...(canShiftGapTagUpdate && isMembershipLinkedToUnit
      ? [
          {
            key: 'actions',
            title: 'Actions',
            width: '5%',
            render: (_: unknown, tag: UnitShiftGapTagType) => (
              <Dropdown
                menu={{
                  items: buildMenuItemsByTag(tag),
                }}
                trigger={['click']}
              >
                <Button
                  type="text"
                  shape="circle"
                  icon={
                    <Icon
                      size="lg"
                      icon="verticalEllipsis"
                      kind="regular"
                      color="elm"
                    />
                  }
                />
              </Dropdown>
            ),
          },
        ]
      : []),
  ];

  if (error) {
    return <ErrorHandler error={error} log="UnitShiftGapTagsTable" />;
  }

  return (
    <>
      {canShiftGapTagCreate && isMembershipLinkedToUnit && (
        <Portal
          targetId="wk-portal-target:location-shift-tags-table-extra-actions"
          triggerRender={triggerPortal}
        >
          <Gutter size="sm" direction="bottom">
            <AddButton onClick={openCreateDrawer}>Shift Tag</AddButton>
          </Gutter>
        </Portal>
      )}
      <RenderFilteredTable
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={unitTags}
        filters={{}}
        scroll={{ x: undefined }}
        onClearAllFilters={() => {}}
        onClearFilter={() => {}}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
        }}
        rowClassName={(record) =>
          record.archivedAt ? 'disabled-row' : undefined
        }
      />
    </>
  );
}

function buildMenuItems({
  tag,
  onArchive,
  onUnarchive,
  onEdit,
}: {
  tag: UnitShiftGapTagType;
  onArchive: () => void;
  onUnarchive: () => void;
  onEdit: () => void;
}) {
  const isArchived = Boolean(tag.archivedAt);

  if (isArchived) {
    return [
      {
        key: 'unarchive',
        label: 'Unarchive',
        onClick: onUnarchive,
      },
    ];
  }

  return [
    {
      key: 'edit',
      label: 'Edit',
      onClick: onEdit,
    },
    {
      key: 'archive',
      label: 'Archive',
      onClick: () => handleArchiveConfirmation(onArchive),
    },
  ];
}

function handleArchiveConfirmation(onConfirm: () => void) {
  Modal.confirm({
    title: 'Are you sure you want to archive this shift tag?',
    content:
      "This tag will be hidden from existing shifts and can't be used for future shifts.",
    okText: 'Archive',
    danger: true,
    onOk: onConfirm,
  });
}
